import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Sparkles, Trash2, NotebookPen, Download, Plus } from "lucide-react";
import { toast } from "sonner";
import ContentRequired from "@/components/ContentRequired";
import { useConfirm } from "@/context/ConfirmContext";

const DEFAULT_PROMPTS = [
  "What are we most proud of doing today?",
  "What could we have done better?",
  "How will we improve tomorrow?",
];

export default function Journal() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [days, setDays] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [dayId, setDayId] = useState("");
  const [selectedPrompt, setSelectedPrompt] = useState("");
  const [content, setContent] = useState("");
  const [aiPrompts, setAiPrompts] = useState([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminForm, setAdminForm] = useState({ text: "", day_id: "", order: 0 });

  const isAdmin = user?.role === "admin" || user?.role === "faculty";

  const load = async () => {
    const [j, d, p] = await Promise.all([
      api.get("/journal").then((x) => x.data),
      api.get("/days").then((x) => x.data),
      api.get("/journal-prompts").then((x) => x.data),
    ]);
    setEntries(j); setDays(d); setPrompts(p);
  };
  useEffect(() => { load(); }, []);

  // Day-specific prompts + generic + AI-generated, in that order
  const promptOptions = useMemo(() => {
    const dayPrompts = prompts.filter((p) => p.day_id === dayId).map((p) => ({ text: p.text, source: "Day prompt" }));
    const generic = prompts.filter((p) => !p.day_id).map((p) => ({ text: p.text, source: "Generic" }));
    const ai = aiPrompts.map((t) => ({ text: t, source: "AI" }));
    const defaults = DEFAULT_PROMPTS.map((t) => ({ text: t, source: "Default" }));
    const all = [...dayPrompts, ...generic, ...ai];
    return all.length ? all : defaults.map((d) => ({ ...d, source: "Default" }));
  }, [prompts, dayId, aiPrompts]);

  const save = async () => {
    if (!selectedPrompt) return toast.error("Choose a prompt to respond to");
    if (!content.trim()) return toast.error("Write a response first");
    try {
      await api.post("/journal", { content, prompt: selectedPrompt, day_id: dayId || null });
      setContent(""); setSelectedPrompt("");
      toast.success("Reflection saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  const generatePrompts = async () => {
    setAiBusy(true);
    try {
      const day = days.find((x) => x.id === dayId);
      const { data } = await api.post("/ai/reflection-prompt", {
        day_title: day?.title || null,
        day_theme: day?.theme || null,
      });
      // The AI returns a text blob with 3 numbered prompts; split them
      const lines = String(data.prompts || "").split(/\n+/).map((l) => l.replace(/^\d+[\.\)]\s*/, "").trim()).filter(Boolean);
      setAiPrompts(lines.slice(0, 5));
      toast.success("AI prompts ready — pick one below");
    } catch (e) { toast.error("Could not generate prompts"); }
    finally { setAiBusy(false); }
  };

  const remove = async (id) => {
    if (!(await confirm({ title: "Delete this entry?", destructive: true }))) return;
    await api.delete(`/journal/${id}`);
    load();
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(entries, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `journal-${user.id}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = async () => {
    try {
      const res = await api.get("/journal/export", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const safe = (user.name || user.email.split("@")[0]).replace(/\s+/g, "_");
      a.href = url; a.download = `weave-journal-${safe}-${today}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF export failed");
    }
  };

  const adminAddPrompt = async () => {
    if (!adminForm.text.trim()) return toast.error("Prompt text required");
    try {
      await api.post("/journal-prompts", { ...adminForm, day_id: adminForm.day_id || null, order: Number(adminForm.order || 0) });
      setAdminOpen(false);
      setAdminForm({ text: "", day_id: "", order: 0 });
      toast.success("Prompt added");
      load();
    } catch (e) { toast.error("Could not add"); }
  };

  const adminRemovePrompt = async (id) => {
    if (!(await confirm({ title: "Delete this prompt?", destructive: true }))) return;
    await api.delete(`/journal-prompts/${id}`);
    load();
  };

  return (
    <div className="space-y-8" data-testid="journal-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Learning journal — individual</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">Reflect on the work</h1>
          <p className="text-[#525252] mt-2 max-w-2xl text-sm">
            Pick a prompt and write your individual reflection. Journals are private to you and faculty; the team-wide accumulated learning belongs in the CEO daily report.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-none border-[#0A0A0A]" onClick={exportPdf} data-testid="journal-export-pdf-btn">
            <Download size={14} className="mr-2" /> Export PDF
          </Button>
          <Button variant="outline" className="rounded-none border-[#0A0A0A]" onClick={exportJson} data-testid="journal-export-btn">
            <Download size={14} className="mr-2" /> JSON
          </Button>
          {isAdmin && (
            <Dialog open={adminOpen} onOpenChange={setAdminOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]" data-testid="prompt-add-btn">
                  <Plus size={14} className="mr-2" /> Add prompt
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none">
                <DialogHeader>
                  <DialogTitle className="font-display tracking-tight">New journal prompt</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className="label-eyebrow">Prompt text</Label>
                    <Textarea className="mt-2 rounded-none min-h-[80px]" value={adminForm.text} onChange={(e) => setAdminForm((f) => ({ ...f, text: e.target.value }))} placeholder="What did you learn today about your team's culture under pressure?" data-testid="prompt-text-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="label-eyebrow">Day (optional)</Label>
                      <select className="mt-2 w-full h-11 border border-[#E5E7EB] bg-white px-3 text-sm" value={adminForm.day_id} onChange={(e) => setAdminForm((f) => ({ ...f, day_id: e.target.value }))} data-testid="prompt-day-select">
                        <option value="">— Generic (any day) —</option>
                        {days.map((d) => <option key={d.id} value={d.id}>Day {d.day_number} · {d.title || "Untitled"}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="label-eyebrow">Order</Label>
                      <Input type="number" value={adminForm.order} onChange={(e) => setAdminForm((f) => ({ ...f, order: e.target.value }))} className="mt-2 rounded-none h-11" />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={adminAddPrompt} className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]" data-testid="prompt-save-btn">Add prompt</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Day selector */}
      {days.length > 0 && (
        <div>
          <div className="label-eyebrow mb-2">Day</div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="journal-day-picker">
            <button onClick={() => setDayId("")} className={`px-3 py-1.5 text-sm border ${dayId === "" ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E7EB] bg-white hover:border-[#0A0A0A]"}`}>None</button>
            {days.map((d) => (
              <button key={d.id} onClick={() => { setDayId(d.id); setSelectedPrompt(""); }} data-testid={`journal-day-pick-${d.day_number}`} className={`px-3 py-1.5 text-sm border ${dayId === d.id ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E7EB] bg-white hover:border-[#0A0A0A]"}`}>
                Day {d.day_number}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Prompt picker */}
      <div className="border border-[#0A0A0A] bg-white p-6 space-y-4" data-testid="journal-editor">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="label-eyebrow">Pick a prompt</div>
            <p className="text-xs text-[#525252] mt-1">Click one of the prompts below, or generate AI prompts tailored to this day.</p>
          </div>
          <Button onClick={generatePrompts} disabled={aiBusy} variant="outline" className="rounded-none border-[#0A0A0A]" data-testid="journal-ai-prompts-btn">
            <Sparkles size={14} className="mr-2" /> {aiBusy ? "Generating…" : "AI prompts"}
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {promptOptions.map((p, i) => {
            const active = selectedPrompt === p.text;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setSelectedPrompt(p.text)}
                data-testid={`prompt-option-${i}`}
                className={`text-left p-4 border transition-colors ${
                  active ? "border-[#2596be] bg-[#E6F4FA]" : "border-[#E5E7EB] bg-white hover:border-[#0A0A0A]"
                }`}
              >
                <div className="label-eyebrow flex items-center gap-1 text-[10px]">
                  {p.source === "AI" && <Sparkles size={10} />} {p.source}
                </div>
                <div className="mt-2 text-sm font-medium">{p.text}</div>
              </button>
            );
          })}
        </div>

        {selectedPrompt && (
          <>
            <div className="border-t border-[#E5E7EB] pt-4">
              <Label className="label-eyebrow">Your reflection</Label>
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write your individual reflection in response to the prompt above…"
                className="rounded-none min-h-[180px] mt-2"
                data-testid="journal-content-input"
              />
            </div>
            <div className="flex justify-between items-center">
              <div className="text-xs text-[#525252] italic max-w-md">
                Responding to: <span className="text-[#0A0A0A] font-medium">"{selectedPrompt}"</span>
              </div>
              <Button onClick={save} className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]" data-testid="journal-save-btn">
                <NotebookPen size={14} className="mr-2" /> Save entry
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Admin prompt list */}
      {isAdmin && prompts.length > 0 && (
        <div>
          <div className="label-eyebrow mb-3">Configured prompts ({prompts.length})</div>
          <div className="border border-[#E5E7EB] bg-white">
            {prompts.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#E5E7EB] last:border-b-0" data-testid={`admin-prompt-${p.id}`}>
                <div className="flex-1">
                  <div className="text-sm">{p.text}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#525252] mt-1">
                    {p.day_id ? `Day ${days.find((d) => d.id === p.day_id)?.day_number || "?"}` : "Generic"} · order {p.order ?? 0}
                  </div>
                </div>
                <button onClick={() => adminRemovePrompt(p.id)} className="text-[#525252] hover:text-[#e93e36]" data-testid={`admin-prompt-delete-${p.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Entries */}
      {entries.length === 0 ? (
        <ContentRequired
          title="No journal entries yet"
          description="Pick a prompt above and write your first reflection. Faculty can add day-specific prompts that will show up here."
          testid="journal-empty-content-required"
        />
      ) : (
        <div className="space-y-4">
          <div className="label-eyebrow">Your entries ({entries.length})</div>
          {entries.map((e) => (
            <div key={e.id} className="border border-[#E5E7EB] bg-white p-5" data-testid={`journal-entry-${e.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3 text-xs text-[#525252]">
                  <span>{e.created_at?.slice(0, 16).replace("T", " ")}</span>
                  {e.day_id && days.find((d) => d.id === e.day_id) && (
                    <span className="border border-[#E5E7EB] px-2 py-0.5">Day {days.find((d) => d.id === e.day_id).day_number}</span>
                  )}
                </div>
                <button onClick={() => remove(e.id)} className="text-[#525252] hover:text-[#e93e36]" data-testid={`journal-delete-${e.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
              {e.prompt && <div className="mt-3 label-eyebrow text-[#2596be] !text-xs normal-case tracking-normal font-medium">"{e.prompt}"</div>}
              <p className="mt-2 text-sm whitespace-pre-wrap text-[#0A0A0A] leading-relaxed">{e.content}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
